/**
 * ImageEditing Module
 * Provides image transformation (rotate, scale, translate, filters) and export as PNG download.
 */

interface ImageTransformParams {
    source: string;
    saturation: number;
    contrast: number;
    grayscale: number;
    scaleX: number;
    scaleY: number;
    translateX: number;
    translateY: number;
    rotate: number;
    outputWidth: number;
    outputHeight: number;
}

const ImageEditingModule = {
    /**
     * Transforms an image using Canvas and returns the result as Base64-encoded PNG string.
     * @param params - Transformation parameters
     * @returns Base64-encoded PNG data (without data URL prefix)
     */
    async transformAndExport(params: ImageTransformParams): Promise<string> {
        const { source, saturation, contrast, grayscale, scaleX, scaleY, translateX, translateY, rotate, outputWidth, outputHeight } = params;

        const img = new Image();
        img.crossOrigin = 'anonymous';

        await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = () => reject(new Error(`Failed to load image: ${source}`));
            img.src = source;
        });

        const canvas = document.createElement('canvas');
        canvas.width = outputWidth;
        canvas.height = outputHeight;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
            throw new Error('Could not get 2D canvas context');
        }

        ctx.filter = `saturate(${saturation}) contrast(${contrast}) grayscale(${grayscale})`;

        // Mittelpunkt des Canvas als Referenzpunkt
        const cx = outputWidth / 2;
        const cy = outputHeight / 2;

        ctx.translate(cx, cy);
        ctx.scale(scaleX, scaleY);
        ctx.translate(translateX, translateY);
        ctx.rotate((rotate * Math.PI) / 180);
        ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2, img.naturalWidth, img.naturalHeight);

        const dataUrl = canvas.toDataURL('image/png');
        return dataUrl.split(',')[1] ?? '';
    },

    /**
     * Downloads a .NET stream as a file in the browser.
     * @param streamRef - A DotNetStreamReference from Blazor
     * @param fileName - The download file name
     * @param contentType - MIME type of the file
     */
    async downloadFile(streamRef: { arrayBuffer(): Promise<ArrayBuffer> }, fileName: string, contentType: string): Promise<void> {
        const arrayBuffer = await streamRef.arrayBuffer();
        const blob = new Blob([arrayBuffer], { type: contentType });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }
};

export default ImageEditingModule;
